import { Dropdown, Option } from '@fluentui/react-components';
import { useNavigate } from 'react-router-dom';

import { useConfirmLeave } from '../components/unsavedGuard';

import { tenantPath, useMyTenants } from './useMyTenants';

export interface TenantSwitcherProps {
  /** Slug of the tenant currently open (`/t/:slug/...`). */
  currentSlug: string;
  /** The screen segment after the slug (e.g. `devices`), kept across the switch. */
  screen: string;
}

/**
 * Header dropdown that moves the SAME screen to another tenant. Rendered only
 * when there is a choice to make (more than one tenant, or a platform admin
 * who can enter any tenant). Goes through the unsaved-changes guard like the
 * nav tabs do.
 */
export function TenantSwitcher({ currentSlug, screen }: TenantSwitcherProps): JSX.Element | null {
  const my = useMyTenants();
  const navigate = useNavigate();
  const confirmLeave = useConfirmLeave();

  if (my.data === null) {
    return null;
  }
  const { tenants, isPlatformAdmin } = my.data;
  if (tenants.length <= 1 && !isPlatformAdmin) {
    return null;
  }
  const current = tenants.find((t) => t.slug === currentSlug);

  return (
    <Dropdown
      aria-label="Switch tenant"
      size="small"
      value={current?.name ?? currentSlug}
      selectedOptions={[currentSlug]}
      onOptionSelect={(_event, data) => {
        const slug = data.optionValue;
        if (slug === undefined || slug === currentSlug) {
          return;
        }
        if (confirmLeave()) {
          void navigate(tenantPath(slug, screen === '' ? 'site-config' : screen));
        }
      }}
    >
      {tenants.map((t) => (
        <Option key={t.id} value={t.slug}>
          {`${t.name} (${t.slug})`}
        </Option>
      ))}
    </Dropdown>
  );
}
